// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React, { SyntheticEvent, useState } from "react";
import { Link } from "react-router-dom";
import { FontIcon } from "@fluentui/react";
import { Spinner, SpinnerSize } from "@fluentui/react/lib/Spinner";
import "./condensedList.scss";

/**
 * Properties for Condensed List Component
 * @member title - Title of condensed list
 * @member items - Array of items to be rendered
 * @member newLinkTo - Link for list items
 * @member newLinkToTitle - Title of newLink
 * @member onClick - Function to call on clicking items
 * @member onDelete - Function to call on deleting items
 * @member Component - Component to be rendered for list items
 */
interface ICondensedListProps {
    title?: string;
    Component?: any;
    items: any[];
    newLinkTo?: string;
    newLinkToTitle?: string;
    searchPlaceholder?: string;
    onClick?: (item) => void;
    onDelete?: (item) => void;
    onSearch?: (keyword: string) => void;
    children?: any;
}

/**
 * @name - Condensed List
 * @description - Clickable, deletable and linkable list of items
 */
const CondensedList = ({
   title,
   items,
   newLinkTo,
   newLinkToTitle,
   searchPlaceholder = 'Search',
   Component,
   onClick,
   onDelete,
   onSearch,
   children,
}: ICondensedListProps) => {
    const [ currentId, setCurrentId ] = useState<string>(null);

    const onItemClick = (e: SyntheticEvent, item: any) => {
        setCurrentId(item.id);

        onClick?.(item);
    };

    const onItemDelete = (e: SyntheticEvent, item: any) => {
        e.stopPropagation();
        e.preventDefault();

        onDelete?.(item);
    };

    const getItemProps = (item) => ({
        onClick: (e) => onItemClick(e, item),
        onDelete: (e) => onItemDelete(e, item),
        key: item.id || item.uid,
        currentId,
        item,
    });

    return (
        <div className="condensed-list">
            {(title || newLinkTo) && (
                <div className="condensed-list-header bg-darker-2">
                    {title && (
                        <span>{title}</span>
                    )}
                    {newLinkTo && (
                        <Link to={newLinkTo} className="float-right add-button" role="button" title={newLinkToTitle}
                              id="addConnection">
                            <FontIcon iconName="AddTo" />
                        </Link>
                    )}
                </div>
            )}
            <div className="condensed-list-body">
                {onSearch && (
                    <div className='condensed-list-body-searchbar'>
                        <input
                            type='text'
                            placeholder={searchPlaceholder}
                            className='w-100'
                            onChange={(event) => onSearch(event.target.value)}
                        />
                    </div>
                )}
                {!items && (
                    <div className="p-3 text-center">
                        <Spinner size={SpinnerSize.small} />
                    </div>
                )}
                {(items && items.length === 0) && (
                    <div className="p-3 text-center">No items found</div>
                )}
                {(items && items.length > 0) && (
                    <ul className="condensed-list-items">
                        {items.map((item) => children ? (
                            children(getItemProps(item))
                        ) : (
                            <Component {...getItemProps(item)} />
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

/**
 * Generic list item with an onClick function and a name
 * @param param0 - {item: {name: ""}, onClick: (item) => void;}
 */
export function ListItem({ item, onClick, currentId }) {
    return (
        <li>
            {/* eslint-disable-next-line */}
            <a className={["condensed-list-item", currentId === item.id? "current":""].join(" ")} onClick={onClick}>
                <span className="px-2">{item.name}</span>
            </a>
        </li>
    );
}

export default CondensedList;
